/** QuickRouter — Dossier de fabrication complet : Gerber, perçages, nomenclature et
 *  fichier de placement au format JLCPCB, notice de commande. */
import { gerberLayers } from './gerber.js';
import { drillFiles } from './excellon.js';
import { makeZip } from './zip.js';
import { polyBox } from '../geom.js';
import { copperLayers, layerRole, isOuter } from '../layers.js';
import { stackupOf } from '../stackup.js';
import { netRule } from '../netclasses.js';

const EXT = {
  // Couches internes : extensions de KiCad (.g2 … .g5) que reconnaissent JLCPCB et PCBWay.
  In1_Cu: 'g2', In2_Cu: 'g3', In3_Cu: 'g4', In4_Cu: 'g5',
  F_Cu: 'gtl', B_Cu: 'gbl', F_Mask: 'gts', B_Mask: 'gbs', F_Paste: 'gtp', B_Paste: 'gbp',
  F_Silkscreen: 'gto', B_Silkscreen: 'gbo', Edge_Cuts: 'gm1',
};

export function slugOf(name) {
  return String(name ?? 'carte').normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9_-]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60) || 'carte';
}

const csvCell = (v) => {
  const s = String(v ?? '');
  return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const csv = (rows) => `${rows.map((r) => r.map(csvCell).join(',')).join('\r\n')}\r\n`;

/** Tri naturel des repères : R2 avant R10. */
const natural = (a, b) => a.localeCompare(b, 'fr', { numeric: true });

/** Nomenclature JLCPCB : une ligne par (valeur, empreinte, LCSC). Non montés et trous exclus. */
export function bomCsv(project, lookup) {
  const groups = new Map();
  for (const c of project.components) {
    const fp = lookup(c.footprint);
    if (c.dnp || !fp || fp.refPrefix === 'H') continue;
    const comment = c.value || c.mpn || fp.name;
    const key = `${comment}|${fp.id}|${c.lcsc}`;
    if (!groups.has(key)) groups.set(key, { comment, footprint: fp.id, lcsc: c.lcsc, refs: [] });
    groups.get(key).refs.push(c.ref);
  }
  const rows = [...groups.values()]
    .map((g) => ({ ...g, refs: g.refs.sort(natural) }))
    .sort((a, b) => natural(a.refs[0], b.refs[0]));
  return { csv: csv([['Comment', 'Designator', 'Footprint', 'LCSC Part #'], ...rows.map((g) => [g.comment, g.refs.join(','), g.footprint, g.lcsc])]), lines: rows.length, rows };
}

/** Fichier de placement (CPL) : CMS seulement, origine du composant = centre. */
export function cplCsv(project, lookup) {
  const rows = [];
  for (const c of [...project.components].sort((a, b) => natural(a.ref, b.ref))) {
    const fp = lookup(c.footprint);
    if (c.dnp || !fp || !fp.pads.length || fp.pads.some((p) => p.drill)) continue;
    rows.push([c.ref, `${c.pos[0].toFixed(4)}mm`, `${c.pos[1].toFixed(4)}mm`, c.side === 'bottom' ? 'Bottom' : 'Top', String(Math.round(((c.rot % 360) + 360) % 360 * 100) / 100)]);
  }
  return csv([['Designator', 'Mid X', 'Mid Y', 'Layer', 'Rotation'], ...rows]);
}

/** Empilage et impédances visées (multicouche ou nets à impédance contrôlée). */
function stackupLines(project) {
  const ctrl = project.nets.filter((n) => n.impedance || n.netclass);
  if (project.board.layers === 2 && !ctrl.length) return [];
  const st = stackupOf(project.board);
  const lines = [`  Empilage            : ${st.id} — ${st.name} (à saisir tel quel à la commande)`];
  for (const x of st.stack) lines.push(x.type === 'copper' ? `      ${x.name.padEnd(4)} cuivre ${x.t} mm` : `           ${x.material} ${x.t} mm, εr ${x.er}`);
  if (ctrl.length) {
    lines.push('  Impédance contrôlée : OUI (à cocher à la commande) — valeurs visées :');
    const seen = new Set();
    for (const n of ctrl) {
      const r = netRule(project, n, 'F');
      const key = `${n.netclass ?? ''}|${r?.impedance}|${r?.diff}`;
      if (!r || seen.has(key)) continue;
      seen.add(key);
      lines.push(`      ${n.netclass ?? n.name} : ${r.impedance} Ω ${r.diff ? 'différentiel' : 'simple'} — en surface w ${r.width} mm${r.gap ? `, écart ${r.gap} mm` : ''} (calcul ±10 %, à confirmer au calculateur JLCPCB)`);
    }
  }
  return lines;
}

function readme(project, slug, counts) {
  const [x0, y0, x1, y1] = polyBox(project.board.outline);
  const r = project.rules;
  return [
    `QuickRouter — dossier de fabrication « ${project.name} »`,
    `Généré le ${new Date().toISOString().slice(0, 16).replace('T', ' ')} UTC`,
    '',
    'PARAMÈTRES DE COMMANDE',
    `  Couches cuivre      : ${project.board.layers}`,
    ...stackupLines(project),
    `  Dimensions hors tout: ${(x1 - x0).toFixed(2)} × ${(y1 - y0).toFixed(2)} mm`,
    `  Épaisseur           : ${project.board.thickness} mm`,
    `  Couleur du vernis   : ${project.board.color}`,
    `  Finition            : ${project.board.finish}`,
    `  Piste mini          : ${Math.min(r.trackWidth, ...project.tracks.map((t) => t.width))} mm`,
    `  Isolement mini      : ${r.clearance} mm`,
    `  Perçage mini        : ${r.minDrill} mm (vias ${r.viaDrill} / ${r.viaDiameter} mm, recouverts de vernis)`,
    `  Trous               : ${counts.pth} métallisés, ${counts.npth} non métallisés`,
    '',
    'FICHIERS',
    `  ${slug}-F_Cu.gtl / -B_Cu.gbl          cuivre dessus / dessous`,
    ...copperLayers(project.board).filter((L) => !isOuter(L)).map((L) => `  ${slug}-${L}_Cu.${EXT[`${L}_Cu`]}${' '.repeat(Math.max(1, 24 - slug.length))}cuivre interne ${L.slice(2)} (${layerRole(project.board, L) === 'plane' ? 'plan' : 'signal'})`),
    `  ${slug}-F_Mask.gts / -B_Mask.gbs      vernis épargne (ouvertures)`,
    `  ${slug}-F_Paste.gtp / -B_Paste.gbp    pochoir de pâte à braser`,
    `  ${slug}-F_Silkscreen.gto / -B_...gbo  sérigraphie`,
    `  ${slug}-Edge_Cuts.gm1                 contour de découpe`,
    `  ${slug}-PTH.drl / -NPTH.drl           perçages Excellon (métriques)`,
    `  ${slug}-BOM.csv / -CPL.csv            nomenclature et placement (format JLCPCB)`,
    '',
    'REMARQUES',
    '  Zones de cuivre reliées en plein (pas de pastilles thermiques).',
    '  Rotations du CPL = rotations QuickRouter : la correction propre au fabricant reste à vérifier sur son aperçu.',
    '',
  ].join('\r\n');
}

/** Tous les fichiers de fabrication : { files: [{ name, data }], warnings }. */
export function fabricationFiles(project, lookup) {
  const slug = slugOf(project.name);
  const warnings = [];
  const missing = project.components.filter((c) => !lookup(c.footprint)).map((c) => `${c.ref} (${c.footprint})`);
  if (missing.length) warnings.push(`empreinte introuvable, composant ignoré : ${missing.join(', ')}`);
  const layers = gerberLayers(project, lookup);
  const files = Object.entries(layers).map(([k, data]) => ({ name: `${slug}-${k}.${EXT[k]}`, data }));
  const drills = drillFiles(project, lookup);
  warnings.push(...drills.warnings);
  files.push({ name: `${slug}-PTH.drl`, data: drills.pth }, { name: `${slug}-NPTH.drl`, data: drills.npth });
  const bom = bomCsv(project, lookup);
  const noLcsc = project.components.filter((c) => !c.dnp && !c.lcsc && lookup(c.footprint)?.refPrefix !== 'H').map((c) => c.ref);
  if (noLcsc.length) warnings.push(`sans référence LCSC (assemblage JLCPCB impossible pour eux) : ${noLcsc.sort(natural).join(', ')}`);
  files.push({ name: `${slug}-BOM.csv`, data: bom.csv }, { name: `${slug}-CPL.csv`, data: cplCsv(project, lookup) });
  files.push({ name: 'LISEZMOI.txt', data: readme(project, slug, drills.counts) });
  return { files, warnings, slug };
}

export function fabricationZip(project, lookup) {
  const { files, warnings, slug } = fabricationFiles(project, lookup);
  return { zip: makeZip(files), warnings, name: `${slug}-fabrication.zip` };
}
